import {useEffect, useMemo, useState} from "react";
import Navbar from "../Components/Navbar";
import ContactFooter from "../Components/ContactFooter";
import TrashCan from "../Images/TrashCan.svg";
import "../Styles/ViewStyles/basket.css";
import {useFormValidation} from "../Validation/UseFormValidation";
import {createBasketFormScheme} from "../Validation/FormSchemes/BasketFormScheme";
import {ErrorMessage} from "../Components/ErrorMessage";
import {emptyBasket, readBasket, removeBasketItem} from "../Services/BasketService";
import {BasketItem} from "../Types/BasketItem";
import {createReservationPayload, makeReservation} from "../Services/ReservationService";

export function Basket() {
  const [items, setItems] = useState<BasketItem[]>([]);

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
    postcode: "",
    address: "",
    honeyPot: "",
  });

  const [isDelivery, setIsDelivery] = useState(false);

  const {errors, validateForm, resetErrors} = useFormValidation(createBasketFormScheme(isDelivery));
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState("");

  useEffect(() => {
    setItems(readBasket());
  }, []);

  const handleChange = (e: { target: { name: any; value: any; }; }) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const total = useMemo(() => items.reduce((s, it) => s + it.price * it.quantity, 0), [items]);

  function handleDeliveryChange(nextIsDelivery: boolean) {
    setIsDelivery(nextIsDelivery);
    setConfirmation("");
    resetErrors();

    if (!nextIsDelivery) {
      setFormData((prev) => ({
        ...prev,
        postcode: "",
        address: "",
      }));
    }
  }

  async function handleReservation() {
    setConfirmation("");
    setError("");

    const dataToValidate = {
      ...formData,
      postcode: isDelivery ? formData.postcode : "",
      address: isDelivery ? formData.address : "",
    };

    const valid = validateForm(dataToValidate);
    if (!valid) {
      return;
    }

    try {
      setConfirmation("Reservering verzonden!");
      resetErrors();

      const payload = createReservationPayload(items, formData, isDelivery);
      const response = await makeReservation(payload);
      
      if (!response.ok) {
        if (response.status === 429) {
          setError("Te veel reserveringen gemaakt.")
        } else {
          setError("Reservering mislukt, probeer het later opnieuw.");
        }
        setConfirmation("");

        throw new Error("Failed to make reservation");
      }

      emptyBasket();
      setItems([]);

      setFormData(prev => {
        const cleared = {...prev};
        Object.keys(cleared).forEach((k) => {
          (cleared as any)[k] = "";
        });
        return cleared;
      });
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <>
      <Navbar/>

      <div className="white-text-block white-text-block--center">
        <h1 className="header-1">Mandje</h1>
        <p>Reserveer voor donderdagavond om het op vrijdag op te kunnen halen of te laten bezorgen.
        </p>
      </div>
      
      <div className="white-text-block white-text-block--center">
        {items.length === 0 ? (
          <p>Je mandje is leeg.</p>
        ) : (
          <>
            <div className="table-scroll-box">
              <table className="simple-table">
                <thead>
                <tr>
                  <th>Naam</th>
                  <th>Prijs per stuk</th>
                  <th>Aantal</th>
                  <th>Subtotaal</th>
                </tr>
                </thead>
                <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.name}</td>
                    <td>€{it.price.toFixed(2).replace('.', ',')}</td>
                    <td>{it.quantity}</td>
                    <td>€{(it.price * it.quantity).toFixed(2).replace('.', ',')}</td>
                    <td style={{minWidth: 48, textAlign: 'center'}}>
                      <img className="delete-icon" src={TrashCan} alt="Delete item"
                           onClick={() => setItems(removeBasketItem(it.id))}/>
                    </td>
                  </tr>
                ))}

                <tr>
                  <td colSpan={3}><strong>Totaal</strong></td>
                  <td><strong>€{total.toFixed(2).replace('.', ',')}</strong></td>
                </tr>
                </tbody>
              </table>
            </div>
            
            <div className="form-padding">
              <form onSubmit={(e) => {
                e.preventDefault();
                handleReservation();
              }}>
                <input className="anti-bot-field" type="text" name="honeyPot" value={formData.honeyPot}
                       onChange={handleChange}
                       style={{display: 'none'}}/>

                <ul className="horizontal-form">
                  <li className="form-item">
                    <h3 className="input-label">E-mail</h3>
                    <input className="input-field input-field--long" type="text"
                           name="email" value={formData.email}
                           onChange={handleChange}/>
                    <ErrorMessage error={errors.email}/>
                  </li>
                  <li className="form-item">
                    <h3 className="input-label">Telefoon nummer</h3>
                    <input className="input-field" type="text" name="phone"
                           value={formData.phone}
                           onChange={handleChange}/>
                    <ErrorMessage error={errors.phone}/>
                  </li>
                </ul>

                <ul className="horizontal-form">
                  <li className="form-item">
                    <h3 className="input-label">Voornaam</h3>
                    <input className="input-field" type="text" name="firstName"
                           value={formData.firstName}
                           onChange={handleChange}/>
                    <ErrorMessage error={errors.firstName}/>
                  </li>
                  <li className="form-item">
                    <h3 className="input-label">Achternaam</h3>
                    <input className="input-field" type="text" name="lastName"
                           value={formData.lastName}
                           onChange={handleChange}/>
                    <ErrorMessage error={errors.lastName}/>
                  </li>
                </ul>

                <ul className="horizontal-form">
                  <li className="form-item">
                    <label>
                      <input
                        type="radio"
                        name="deliverOpt"
                        checked={!isDelivery}
                        className="radio-button"
                        onChange={() => handleDeliveryChange(false)}/>
                      Afhalen
                    </label>
                  </li>
                  <li className="form-item">
                    <label>
                      <input
                        type="radio"
                        name="deliverOpt"
                        checked={isDelivery}
                        className="radio-button"
                        onChange={() => handleDeliveryChange(true)}/>
                      Bezorgen
                    </label>
                  </li>
                </ul>

                {isDelivery && (
                  <div className="delivery-fields">
                    <ul className="horizontal-form">
                      <li className="form-item">
                        <h3 className="input-label">Postcode</h3>
                        <input className="input-field input-field--small" name="postcode"
                               value={formData.postcode}
                               onChange={handleChange}/>
                        <ErrorMessage error={errors.postcode}/>
                      </li>
                      <li className="form-item">
                        <h3 className="input-label">Adres</h3>
                        <input className="input-field" name="address"
                               value={formData.address}
                               onChange={handleChange}/>
                        <ErrorMessage error={errors.address}/>
                      </li>
                    </ul>
                  </div>
                )}

                <div className="button-padding">
                  <button className="button" type="submit">Reserveer</button>
                </div>

                {confirmation && (
                  <p className="form-confirmation">{confirmation}</p>
                )}
                <ErrorMessage error={error}/>
              </form>
            </div>
          </>
        )}
      </div>

      <ContactFooter/>
    </>
  );
}

export default Basket;